import { Fragment, FC } from "react";
import { useLocation, Link } from "react-router-dom";
import { useNavigationTransition } from "./useNavigationTransition";
import { Loader } from "@/frontend/@ui/Loader";

export type CustomLabels = Record<string, string>;

interface BreadcrumbItem {
    label: string;
    href?: string;
}

const getBreadcrumbs = (
    pathname: string,
    customLabels: CustomLabels | undefined
): BreadcrumbItem[] => {
    const paths = pathname.split("/").filter(Boolean);
    let currentPath = "";

    const segments = paths
        .map((path, index) => {
            currentPath += `/${path}`;
            let label = path
                .split("-")
                .map(x => x.charAt(0).toUpperCase() + x.slice(1))
                .join(" ");

            // Check for custom label first
            const customLabel = customLabels?.[path];
            if (customLabel) {
                label = customLabel;
            } else {
                // Ignore if path looks like a uuid
                const isUuid = path.split("-").length === 5;
                if (isUuid) {
                    return undefined;
                }
            }

            // Special cases for specific routes
            if (path === "assessments") {
                return { label, href: "/" };
            }
            if (path === "super" && paths[index + 1]) {
                return { label };
            }
            if (path === "firm" && paths[index + 1]) {
                return { label };
            }
            if (path === "versions" && paths[index + 1]) {
                return { label };
            }
            if (path === "evaluations" && paths[index + 1]) {
                return undefined;
            }
            if (path === "teams" && paths[index + 1]) {
                return undefined;
            }

            // end label is current page and doesn't need a link
            if (index === paths.length - 1) {
                return { label };
            }

            return {
                label,
                href: currentPath
            };
        })
        .filter(x => x !== undefined);

    if (segments.length === 0) {
        return [{ label: "Assessments" }];
    }

    return segments;
};

export const HeaderBreadcrumbs: FC<{ customLabels?: CustomLabels }> = ({
    customLabels
}) => {
    const location = useLocation();
    const breadcrumbs = getBreadcrumbs(location.pathname, customLabels);

    return (
        <div className="flex items-center space-x-2 text-xs text-muted-foreground md:text-sm">
            {breadcrumbs.map((breadcrumb, index) => (
                <Fragment key={index}>
                    {index > 0 && <span>/</span>}
                    {breadcrumb.href ? (
                        <Breadcrumb
                            to={breadcrumb.href}
                            label={breadcrumb.label}
                        />
                    ) : (
                        <span>{breadcrumb.label}</span>
                    )}
                </Fragment>
            ))}
        </div>
    );
};

export const Breadcrumb = ({ to, label }: { to: string; label: string }) => {
    const { spread, isPending } = useNavigationTransition({
        to: { _checked: true, path: to } // forcing link to be checked (even though it's not going through proper type checking)
    });
    return (
        <Link to={to} {...spread} className="hover:text-foreground">
            {isPending ? <Loader /> : label}
        </Link>
    );
};
